import type { ProjectDetailsCharacter, ProjectDetailsLocation, ProjectDetailsStoryboardShot } from "@/types/project";
import type { TimelineItemData } from "@/types/timeline";
import { approvedStoryboardShotVideoId, approvedStoryboardShotVideoPosterImageId } from "@/services/storyboardDisplayedImage";

/** Format scene-id (e.g. "scene-1", "scene-a") as Figma shortcut "Sc1" / "Sca". */
export function formatSceneLabel(sceneId: string | undefined, fallbackIndex: number): string {
  const trimmed = sceneId?.trim() ?? "";
  if (!trimmed) {
    return `Sc${fallbackIndex + 1}`;
  }

  const prefixed = /^scene-(.+)$/i.exec(trimmed);
  if (prefixed?.[1]) {
    return `Sc${prefixed[1]}`;
  }

  const digit = trimmed.match(/\d+/)?.[0];
  if (digit) {
    return `Sc${digit}`;
  }

  return `Sc${trimmed}`;
}

/** Format shot-id (e.g. "shot-3", "shot-a") as Figma shortcut "Sh3" / "Sha". */
export function formatShotLabel(shotId: string | undefined, fallbackIndex: number): string {
  const trimmed = shotId?.trim() ?? "";
  if (!trimmed) {
    return `Sh${fallbackIndex + 1}`;
  }

  const prefixed = /^shot-(.+)$/i.exec(trimmed);
  if (prefixed?.[1]) {
    return `Sh${prefixed[1]}`;
  }

  const digit = trimmed.match(/\d+/)?.[0];
  if (digit) {
    return `Sh${digit}`;
  }

  return `Sh${trimmed}`;
}

export function nameById(id: string, list: ReadonlyArray<{ readonly id: string; readonly name: string }>): string {
  return list.find((e) => e.id === id)?.name ?? id;
}

/**
 * Timeline strip: the approved video owns the slot when there is one (its poster plus a play badge),
 * otherwise the approved still with no badge. The play badge appears only for an approved video — what
 * MainSpace happens to be displaying never reaches the strip.
 */
export function buildStoryboardTimelineItems(shots: readonly ProjectDetailsStoryboardShot[] | undefined, activeIndex: number): TimelineItemData[] {
  if (!shots?.length) {
    return [];
  }

  return shots.map((shot, index) => {
    const approved0 = shot["approved-timeline-images"]?.[0];
    const approvedFrameId = typeof approved0 === "string" && approved0 !== "" ? approved0 : null;
    const approvedVideoId = approvedStoryboardShotVideoId(shot);
    const imageId =
      approvedVideoId === null ? approvedFrameId : approvedStoryboardShotVideoPosterImageId(shot, approvedVideoId) ?? approvedFrameId;

    return {
      id: shot.id ?? String(index),
      index,
      sceneLabel: formatSceneLabel(shot["scene-id"], index),
      shotLabel: formatShotLabel(shot["shot-id"], index),
      title: shot["shot-description"]?.trim() || undefined,
      description: shot["ref-image-prompt"] || shot["shot-name"] || "",
      imageId,
      videoId: approvedVideoId,
      isSelected: index === activeIndex
    };
  });
}

/** Character strip: `approved-images[0]`. */
export function buildCharacterTimelineItems(chars: readonly ProjectDetailsCharacter[] | undefined, activeIndex: number): TimelineItemData[] {
  if (!chars?.length) {
    return [];
  }

  return chars.map((c, index) => {
    const approved0 = c["approved-images"]?.[0];
    const imageId = typeof approved0 === "string" && approved0 !== "" ? approved0 : null;

    return {
      id: c.id,
      index,
      sceneLabel: c.name,
      shotLabel: "",
      description: "",
      imageId,
      videoId: null,
      isSelected: index === activeIndex,
      compactCaption: true
    };
  });
}

/** Timeline selection index after removing the item at `deletedIndex` from a list of `lengthBeforeDelete`. */
export function nextActiveIndexAfterRemoval(deletedIndex: number, currentActive: number, lengthBeforeDelete: number): number {
  const newLength = lengthBeforeDelete - 1;
  if (deletedIndex < currentActive) {
    return currentActive - 1;
  }

  if (deletedIndex === currentActive) {
    return newLength === 0 ? 0 : Math.min(currentActive, newLength - 1);
  }

  return currentActive;
}

/** Location strip: `approved-images[0]`. */
export function buildLocationTimelineItems(locs: readonly ProjectDetailsLocation[] | undefined, activeIndex: number): TimelineItemData[] {
  if (!locs?.length) {
    return [];
  }

  return locs.map((loc, index) => {
    const approved0 = loc["approved-images"]?.[0];
    const imageId = typeof approved0 === "string" && approved0 !== "" ? approved0 : null;

    return {
      id: loc.id,
      index,
      sceneLabel: loc.name,
      shotLabel: "",
      description: "",
      imageId,
      videoId: null,
      isSelected: index === activeIndex,
      compactCaption: true
    };
  });
}
